import React from 'react';
import { Tabs } from 'ui';
import { useIntl } from 'react-intl';
import { FormInstance } from 'antd';
import NotificationPreferences from './index';
import FarmSearchFilterSettings from '../FarmSearchFilterSettings';
import type { PrefMap, Tab } from './types';
import type { FarmSearchFilterMap } from '../FarmSearchFilterSettings';

interface TabbedSettingsProps {
  userForm: FormInstance<PrefMap>;
  farmForm: FormInstance<FarmSearchFilterMap>;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  onSaved?: (data?: Record<string, boolean>) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

function TabbedSettings({
  userForm,
  farmForm,
  activeTab,
  onTabChange,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: TabbedSettingsProps) {
  const intl = useIntl();

  const tabItems = [
    {
      key: 'user',
      label: intl.formatMessage({ id: 'notification.settings.user_tab' }),
      children: (
        <NotificationPreferences
          form={userForm}
          onSaved={onSaved}
          onDirtyChange={activeTab === 'user' ? onDirtyChange : undefined}
          onSavingChange={activeTab === 'user' ? onSavingChange : undefined}
        />
      ),
    },
    {
      key: 'farm',
      label: intl.formatMessage({ id: 'notification.settings.farm_tab' }),
      children: (
        <FarmSearchFilterSettings
          form={farmForm}
          onSaved={(settings: FarmSearchFilterMap) => {
            const adapted: PrefMap = {
              products: settings.products,
              quality_marks: settings.quality_marks,
              distance: settings.distance != null && settings.distance > 0,
              notification_to_farm_email: settings.notification_to_farm_email,
              address: !!settings.address,
            };
            onSaved?.(adapted);
          }}
          onDirtyChange={activeTab === 'farm' ? onDirtyChange : undefined}
          onSavingChange={activeTab === 'farm' ? onSavingChange : undefined}
        />
      ),
    },
  ];

  return (
    <Tabs
      items={tabItems}
      activeKey={activeTab}
      onChange={(key) => onTabChange(key as Tab)}
    />
  );
}

TabbedSettings.displayName = 'TabbedSettings';
export default TabbedSettings;
